import * as React from "react";
import { useContext, useEffect } from "react";

import { IUnitRegistry } from "../../Navigation/UnitRegistry/IUnitRegistry";
import { AttachmentFormRenderer, NavigationSettings } from "../../AttachmentForm/AttachmentFormRenderer";
import { IPublicForm } from "../../../../Forms/PublicForm/IPublicForm";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { ATTACHMENT_FORM_CLEAR_OPENED, ATTACHMENT_FORM_REMOVE_OPENED } from "../../../metricsAction";
import { IsFilledContext } from "../Internals/OptionalPages/IsFilledContext";
import { AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { UnitId } from "../UnitId/UnitId";
import { AttachInfo } from "../../../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { ViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { EvaluableProp } from "../../../withEvaluators";
import { OpenAttachmentAction } from "../../AttachmentForm/AttachmentForm";
import { DisableUnitActionsContext } from "../DisableUnitActionsContext";
import { ExtraPageActionAttachment } from "../../../../Forms/Client/Client";
import { SetUnitItemExtraInfo } from "../Types";
import { AttachmentFormContainer as AttachmentForm } from "../../AttachmentForm/AttachmentFormContainer";

export interface DownloadedFileInfo {
    contentUri?: string;
    filename?: string;
    isFile?: boolean;
}

export interface AttachmentFormLayoutProps {
    unitId: UnitId;
    unitRegistry: IUnitRegistry;
    gfv: string;
    caption: string;
    description: string;
    attachmentInfo?: AttachInfo;
    onRemoveAttachment: (bindingPath: AbsoluteResolvedModelPath) => Promise<void>;
    onClearAttachment: (attachmentId: string) => Promise<void>;
    onRenderAttachmentForm: (id: string, publicForm: IPublicForm) => void;
    bindingPath: AbsoluteResolvedModelPath;
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    viewModelSelect: ViewModelSelect;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    navigationSettings: NavigationSettings;
    visible?: EvaluableProp<boolean>;
    disabled?: boolean;
    children?: React.ReactNode;
    openAttachmentAction?: OpenAttachmentAction | null;
    setOpenAttachmentAction?: (text: OpenAttachmentAction | null) => void;
    attachmentInfoRef: React.MutableRefObject<Map<string, DownloadedFileInfo>>;
    isFormInFormNds: boolean;
    switchOnErrorsLogTooltip: (highlanderPath: string, errorId: string, attachmentId: string) => Promise<void>;
    switchOffErrorsLogTooltip: () => void;
    extraPageActions?: ExtraPageActionAttachment[];
    setUnitItemExtraInfo: SetUnitItemExtraInfo;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[];
    emptyUnitTitle?: string;
    emptyUnitDescription?: React.ReactNode;
}

export const AttachmentFormLayout = (props: AttachmentFormLayoutProps): JSX.Element => {
    const track = useMetrics();
    const onRemoveAttachment = (): Promise<void> => {
        track(ATTACHMENT_FORM_REMOVE_OPENED, { attachmentId: props.attachmentInfo?.id, gfv: props.gfv });
        return props.onRemoveAttachment(props.bindingPath);
    };

    const onClearAttachment = async (): Promise<void | undefined> => {
        if (!attachmentId) {
            return;
        }

        track(ATTACHMENT_FORM_CLEAR_OPENED, { attachmentId: props.attachmentInfo?.id, gfv: props.gfv });

        await props.onClearAttachment(attachmentId);
    };

    const extraPageActions = props.extraPageActions?.map(action => ({
        ...action,
        handler: () => {
            action.handler(props.bindingPath.toLegacyPath());
        },
    }));

    const attachmentNsId = props.attachmentInfo?.nsId;
    const attachmentDraftId = props.attachmentInfo?.meta?.attachmentDraftId;
    const attachmentId = props.attachmentInfo?.id;
    const attachmentAsDraft = attachmentId && attachmentDraftId && attachmentNsId;
    const isFilled = useContext(IsFilledContext);
    const disableUnitActions = useContext(DisableUnitActionsContext);
    const contentUri = props.attachmentInfo?.contentUri;
    const instancePath = props.bindingPath.toLegacyPath();

    useEffect(() => {
        const attachmentInfo: DownloadedFileInfo = {
            isFile: !attachmentAsDraft,
            contentUri: contentUri ?? "",
            filename: props.attachmentInfo?.meta.fileName ?? props.attachmentInfo?.meta.fileNamePatched,
        };

        props.attachmentInfoRef.current.set(instancePath, attachmentInfo);
    }, [attachmentAsDraft, contentUri]);

    useEffect(() => {
        disableUnitActions?.setDisableUnitActions({
            disableRemove: !isFilled,
            disableClear: true,
            hideExtraActions: !contentUri,
        });
    }, [isFilled, contentUri]);

    return (
        <>
            {attachmentAsDraft ? (
                <AttachmentFormRenderer
                    ns={attachmentNsId}
                    draftId={attachmentDraftId}
                    caption={props.caption}
                    description={props.description}
                    onRemoveAttachment={onRemoveAttachment}
                    onClearAttachment={props.isFormInFormNds ? onClearAttachment : undefined}
                    extraPageActions={extraPageActions}
                    onRenderAttachmentForm={(publicForm: IPublicForm) =>
                        props.onRenderAttachmentForm(attachmentId, publicForm)
                    }
                    attachmentId={attachmentId}
                    navigationSettings={props.navigationSettings}
                    switchOnErrorsLogTooltip={props.switchOnErrorsLogTooltip}
                    switchOffErrorsLogTooltip={props.switchOffErrorsLogTooltip}
                    setUnitItemExtraInfo={props.setUnitItemExtraInfo}
                    attachmentUnitItemInfoPaths={props.attachmentUnitItemInfoPaths}
                />
            ) : (
                <AttachmentForm
                    requestedGfv={props.gfv}
                    caption={props.caption}
                    description={props.description}
                    bindingPath={props.bindingPath}
                    disabled={props.disabled}
                    isFilled={isFilled}
                    onRemoveAttachment={onRemoveAttachment}
                    openAttachmentAction={props.openAttachmentAction}
                    setOpenAttachmentAction={props.setOpenAttachmentAction}
                    isFormInFormNds={props.isFormInFormNds}
                    setUnitItemExtraInfo={props.setUnitItemExtraInfo}
                    emptyUnitTitle={props.emptyUnitTitle}
                    emptyUnitDescription={props.emptyUnitDescription}
                />
            )}
        </>
    );
};
